import React, { useMemo, useRef, useState } from "react";
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	Center,
	Flex,
	Icon,
	IconButton,
	Text,
	Tooltip,
} from "@chakra-ui/react";
import { FaCheckCircle } from "react-icons/fa";
import { FiX } from "react-icons/fi";
import { MdBarChart, MdDelete, MdNumbers } from "react-icons/md";
import { PiTarget } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import { ColumnInfo, ModelInfo } from "build-ts/metadata";
import { ColumnType } from "build-ts/enums";
import { useConfigurationContext } from "context/ConfigurationContext";
import KnowledgeGraphVizContainer from "containers/configuration/mkg/KnowledgeGraphVizContainer";
import MetricListComponent from "components/configuration/MetricListComponent";
import BenchmarkListComponent from "components/configuration/BenchmarkListComponent";
import KGColumnListComponent from "components/configuration/KGColumnListComponent";
import OntologyMappingContainer from "containers/configuration/OntologyMappingContainer";
import ModelListContainer from "containers/configuration/ModelListContainer";
import EditMetricDefinitionContainer from "containers/configuration/EditMetricDefinitionContainer";
import EditBenchmarkComponent from "components/configuration/EditBenchmarkComponent";
import EditKGColumnComponent from "components/configuration/EditKGColumnComponent";
import MKGVisualizerContainer from "containers/configuration/mkg/MKGVisualizerContainer";
import SplitView from "components/common/SplitView";
import ThoughtSpotEmbed from "components/configuration/ThoughtSpotEmbed";
import MemoryConfigurationPanel from "components/configuration/MemoryConfigurationPanel";
import DependenciesModal from "components/configuration/DependenciesModal";

// ---------------------------------------------------------------------------
// Detail panels — rendered inside ConfigurationContextProvider, can use context
// ---------------------------------------------------------------------------

const MetricDetailPanel: React.FC<{ onReloadKnowledgeGraph: () => void }> = ({ onReloadKnowledgeGraph }) => {
	const {
		selectedMetric,
		setSelectedMetric,
		editMetricDefinition,
		setEditMetricDefinition,
		viewMKG,
		setViewMKG,
		handleEditMetricDefinitionModal,
		handleViewMKGModal,
		deleteMetricById,
	} = useConfigurationContext();
	const [triggerSubmit, setTriggerSubmit] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const cancelRef = useRef<HTMLButtonElement>(null);

	const closePanel = () => {
		setEditMetricDefinition(false);
		setViewMKG(false);
		setSelectedMetric(null);
	};

	if (!selectedMetric) {
		return (
			<Center h="full" flexDirection="column" gap={3} color="gray.400">
				<Icon as={MdBarChart} boxSize={10} />
				<Text fontSize="sm" mb={0}>Select a metric to edit its definition</Text>
			</Center>
		);
	}

	const isSubgraph = viewMKG && !editMetricDefinition;

	const handleConfirmDelete = async () => {
		setDeleteDialogOpen(false);
		await deleteMetricById(selectedMetric.id!);
	};

	return (
		<>
			<Flex direction="column" h="full">
				{/* Header: title | tab switcher | actions */}
				<Flex
					px={4}
					py={2.5}
					borderBottomWidth="1px"
					borderColor="gray.100"
					justify="space-between"
					align="center"
					flexShrink={0}
					bg="white"
					gap={3}
				>
					<Text fontWeight="700" color="gray.900" fontSize="md" noOfLines={2} mb={0} lineHeight="1.35" textAlign="left" flex={1} minW={0}>
						{selectedMetric.name}
					</Text>

					{/* Tab switcher */}
					<Flex
						bg="gray.100"
						borderRadius="lg"
						p={0.5}
						gap={0.5}
						flexShrink={0}
					>
						<Button
							size="xs"
							borderRadius="md"
							variant="ghost"
							fontWeight={!isSubgraph ? "700" : "500"}
							bg={!isSubgraph ? "white" : "transparent"}
							color={!isSubgraph ? "purple.700" : "gray.500"}
							boxShadow={!isSubgraph ? "sm" : "none"}
							_hover={{ bg: !isSubgraph ? "white" : "gray.200" }}
							onClick={() => handleEditMetricDefinitionModal(selectedMetric)}
							px={3}
						>
							Definition
						</Button>
						<Button
							size="xs"
							borderRadius="md"
							variant="ghost"
							fontWeight={isSubgraph ? "700" : "500"}
							bg={isSubgraph ? "white" : "transparent"}
							color={isSubgraph ? "purple.700" : "gray.500"}
							boxShadow={isSubgraph ? "sm" : "none"}
							_hover={{ bg: isSubgraph ? "white" : "gray.200" }}
							onClick={() => handleViewMKGModal(selectedMetric)}
							px={3}
						>
							Subgraph
						</Button>
					</Flex>

					<Flex gap={1} flexShrink={0} align="center">
						<Tooltip label="Delete metric" placement="top">
							<Button
								aria-label="Delete metric"
								leftIcon={<Icon as={MdDelete} />}
								size="sm"
								variant="ghost"
								colorScheme="red"
								fontWeight="500"
								onClick={() => setDeleteDialogOpen(true)}
							>
								Delete
							</Button>
						</Tooltip>
						<Button
							size="sm"
							colorScheme="green"
							leftIcon={<Icon as={FaCheckCircle} />}
							onClick={() => setTriggerSubmit(true)}
							isDisabled={isSubgraph}
							opacity={isSubgraph ? 0 : 1}
							pointerEvents={isSubgraph ? "none" : "auto"}
						>
							Save
						</Button>
						<Tooltip label="Close panel" placement="top">
							<IconButton
								aria-label="Close panel"
								icon={<Icon as={FiX} boxSize={5} />}
								size="sm"
								variant="ghost"
								colorScheme="gray"
								onClick={closePanel}
							/>
						</Tooltip>
					</Flex>
				</Flex>

				{/* Content */}
				<Box flex={1} overflow="hidden">
					{isSubgraph ? (
						<MKGVisualizerContainer metricId={selectedMetric} />
					) : (
						<Box h="full" overflowY="auto">
							<EditMetricDefinitionContainer
								createMetricFlag={false}
								modelInfo={null}
								metricHeader={selectedMetric}
								onClose={closePanel}
								triggerSubmit={triggerSubmit}
								setTriggerSubmit={setTriggerSubmit}
								onReloadKnowledgeGraph={onReloadKnowledgeGraph}
							/>
						</Box>
					)}
				</Box>
			</Flex>

			<AlertDialog
				isOpen={deleteDialogOpen}
				leastDestructiveRef={cancelRef}
				onClose={() => setDeleteDialogOpen(false)}
				isCentered
			>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							Delete Metric
						</AlertDialogHeader>
						<AlertDialogBody>
							Are you sure you want to delete <strong>{selectedMetric.name}</strong>? This action cannot be undone.
						</AlertDialogBody>
						<AlertDialogFooter>
							<Button ref={cancelRef} onClick={() => setDeleteDialogOpen(false)}>
								Cancel
							</Button>
							<Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
								Delete
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</>
	);
};

const BenchmarkDetailPanel: React.FC<{ onReloadKnowledgeGraph: () => void }> = ({ onReloadKnowledgeGraph }) => {
	const {
		selectedBenchmarkId,
		setSelectedBenchmarkId,
		setSelectedBenchmark,
		setEditBenchmark,
		deleteBenchmarkById,
	} = useConfigurationContext();
	const [triggerSubmit, setTriggerSubmit] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const cancelRef = useRef<HTMLButtonElement>(null);

	const closePanel = () => {
		setEditBenchmark(false);
		setSelectedBenchmark(null);
		setSelectedBenchmarkId(null);
	};

	if (!selectedBenchmarkId) {
		return (
			<Center h="full" flexDirection="column" gap={3} color="gray.400">
				<Icon as={PiTarget} boxSize={10} />
				<Text fontSize="sm" mb={0}>Select a benchmark to edit its definition</Text>
			</Center>
		);
	}

	const handleConfirmDelete = async () => {
		setDeleteDialogOpen(false);
		await deleteBenchmarkById(selectedBenchmarkId.header?.id!);
	};

	return (
		<>
			<Flex direction="column" h="full">
				<Flex px={4} py={2.5} borderBottomWidth="1px" borderColor="gray.100" justify="space-between" align="center" flexShrink={0} bg="white" gap={3}>
					<Text fontWeight="700" color="gray.900" fontSize="sm" noOfLines={2} mb={0} lineHeight="1.35" textAlign="left" flex={1} minW={0}>
						{selectedBenchmarkId.header?.name}
					</Text>
					<Flex gap={1} align="center" flexShrink={0}>
						<Tooltip label="Delete benchmark" placement="top">
							<Button
								aria-label="Delete benchmark"
								leftIcon={<Icon as={MdDelete} />}
								size="sm"
								variant="ghost"
								colorScheme="red"
								fontWeight="500"
								onClick={() => setDeleteDialogOpen(true)}
							>
								Delete
							</Button>
						</Tooltip>
						<Button size="sm" colorScheme="green" leftIcon={<Icon as={FaCheckCircle} />} onClick={() => setTriggerSubmit(true)}>
							Save
						</Button>
						<Tooltip label="Close panel" placement="top">
							<IconButton
								aria-label="Close panel"
								icon={<Icon as={FiX} boxSize={5} />}
								size="sm"
								variant="ghost"
								colorScheme="gray"
								onClick={closePanel}
							/>
						</Tooltip>
					</Flex>
				</Flex>
				<Box flex={1} overflowY="auto">
					<EditBenchmarkComponent
						benchmark={selectedBenchmarkId}
						onClose={closePanel}
						triggerSubmit={triggerSubmit}
						setTriggerSubmit={setTriggerSubmit}
						onReloadKnowledgeGraph={onReloadKnowledgeGraph}
					/>
				</Box>
			</Flex>

			<AlertDialog
				isOpen={deleteDialogOpen}
				leastDestructiveRef={cancelRef}
				onClose={() => setDeleteDialogOpen(false)}
				isCentered
			>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							Delete Benchmark
						</AlertDialogHeader>
						<AlertDialogBody>
							Are you sure you want to delete <strong>{selectedBenchmarkId.header?.name}</strong>? This action cannot be undone.
						</AlertDialogBody>
						<AlertDialogFooter>
							<Button ref={cancelRef} onClick={() => setDeleteDialogOpen(false)}>
								Cancel
							</Button>
							<Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
								Delete
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</>
	);
};

const KGColumnDetailPanel: React.FC<{ onReloadKnowledgeGraph: () => void; onSave: () => void }> = ({
	onReloadKnowledgeGraph,
	onSave,
}) => {
	const {
		selectedKgColumn,
		setSelectedKgColumn,
		setEditColumn,
		deleteKgColumnById,
	} = useConfigurationContext();
	const [triggerSubmit, setTriggerSubmit] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const cancelRef = useRef<HTMLButtonElement>(null);

	const closePanel = () => {
		setEditColumn(false);
		setSelectedKgColumn(null);
	};

	if (!selectedKgColumn) {
		return (
			<Center h="full" flexDirection="column" gap={3} color="gray.400">
				<Icon as={MdNumbers} boxSize={10} />
				<Text fontSize="sm" mb={0}>Select an item to edit its definition</Text>
			</Center>
		);
	}

	const handleConfirmDelete = async () => {
		setDeleteDialogOpen(false);
		await deleteKgColumnById(selectedKgColumn.id!);
	};

	return (
		<>
			<Flex direction="column" h="full">
				<Flex px={4} py={2.5} borderBottomWidth="1px" borderColor="gray.100" justify="space-between" align="center" flexShrink={0} bg="white" gap={3}>
					<Text fontWeight="700" color="gray.900" fontSize="sm" noOfLines={2} mb={0} lineHeight="1.35" textAlign="left" flex={1} minW={0}>
						{selectedKgColumn.name}
					</Text>
					<Flex gap={1} align="center" flexShrink={0}>
						<Tooltip label="Delete column" placement="top">
							<Button
								aria-label="Delete column"
								leftIcon={<Icon as={MdDelete} />}
								size="sm"
								variant="ghost"
								colorScheme="red"
								fontWeight="500"
								onClick={() => setDeleteDialogOpen(true)}
							>
								Delete
							</Button>
						</Tooltip>
						<Button size="sm" colorScheme="green" leftIcon={<Icon as={FaCheckCircle} />} onClick={() => setTriggerSubmit(true)}>
							Save
						</Button>
						<Tooltip label="Close panel" placement="top">
							<IconButton
								aria-label="Close panel"
								icon={<Icon as={FiX} boxSize={5} />}
								size="sm"
								variant="ghost"
								colorScheme="gray"
								onClick={closePanel}
							/>
						</Tooltip>
					</Flex>
				</Flex>
				<Box flex={1} overflowY="auto">
					<EditKGColumnComponent
						id={selectedKgColumn.id}
						onClose={closePanel}
						onSave={onSave}
						triggerSubmit={triggerSubmit}
						setTriggerSubmit={setTriggerSubmit}
						onReloadKnowledgeGraph={onReloadKnowledgeGraph}
					/>
				</Box>
			</Flex>

			<AlertDialog
				isOpen={deleteDialogOpen}
				leastDestructiveRef={cancelRef}
				onClose={() => setDeleteDialogOpen(false)}
				isCentered
			>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							Delete Column
						</AlertDialogHeader>
						<AlertDialogBody>
							Are you sure you want to delete <strong>{selectedKgColumn.name}</strong>? This action cannot be undone.
						</AlertDialogBody>
						<AlertDialogFooter>
							<Button ref={cancelRef} onClick={() => setDeleteDialogOpen(false)}>
								Cancel
							</Button>
							<Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
								Delete
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</>
	);
};

// ---------------------------------------------------------------------------
// MainPanelContent — lives inside ConfigurationContextProvider, reads context
// ---------------------------------------------------------------------------

export interface ConfigPageMainPanelProps {
	activeButton: string;
	shouldReloadKG: boolean;
	modelInfo: ModelInfo | undefined;
	setModelInfo: React.Dispatch<React.SetStateAction<ModelInfo | undefined>>;
	addedColumns: ColumnInfo[];
	removedColumns: ColumnInfo[];
	setAddedColumns: React.Dispatch<React.SetStateAction<ColumnInfo[]>>;
	setRemovedColumns: React.Dispatch<React.SetStateAction<ColumnInfo[]>>;
	fetchModelList: () => Promise<void>;
	handleReloadKnowledgeGraph: () => void;
	tsMatchTsId?: string;
}

const ConfigPageMainPanel: React.FC<ConfigPageMainPanelProps> = ({
	activeButton,
	shouldReloadKG,
	modelInfo,
	setModelInfo,
	addedColumns,
	removedColumns,
	setAddedColumns,
	setRemovedColumns,
	fetchModelList,
	handleReloadKnowledgeGraph,
	tsMatchTsId,
}) => {
	const navigate = useNavigate();
	const {
		selectedMetric,
		editMetricDefinition,
		viewMKG,
		selectedBenchmarkId,
		editBenchmark,
		selectedKgColumn,
		editColumn,
		showDependenciesModal,
		dependencies,
		setShowDependenciesModal,
		setDependencies,
		modelList,
		setModelList,
		kgColumns,
		fetchKgColumns,
	} = useConfigurationContext();

	const measureList = useMemo(
		() => kgColumns?.filter((c) => c.column_type === ColumnType.MEASURE) ?? [],
		[kgColumns],
	);
	const attributeList = useMemo(
		() => kgColumns?.filter((c) => c.column_type === ColumnType.ATTRIBUTE) ?? [],
		[kgColumns],
	);
	const temporalList = useMemo(
		() => kgColumns?.filter((c) => c.column_type === ColumnType.TEMPORAL) ?? [],
		[kgColumns],
	);

	const handleCloseDependenciesModal = () => {
		setShowDependenciesModal(false);
		setDependencies([]);
	};

	const renderMainPanelContent = () => {
		switch (activeButton) {
			case "ViewMKGContainer":
				return <KnowledgeGraphVizContainer shouldReload={shouldReloadKG} />;

			case "metrics": {
				const metricsDetailOpen = (editMetricDefinition || viewMKG) && selectedMetric !== null;
				return (
					<SplitView
						leftPanel={
							<MetricListComponent
								onReloadKnowledgeGraph={handleReloadKnowledgeGraph}
								splitDetailOpen={metricsDetailOpen}
							/>
						}
						rightPanel={<MetricDetailPanel onReloadKnowledgeGraph={handleReloadKnowledgeGraph} />}
						isRightPanelVisible={metricsDetailOpen}
						defaultRightWidth={35}
					/>
				);
			}

			case "benchmarks": {
				const benchmarksDetailOpen = editBenchmark && selectedBenchmarkId !== null;
				return (
					<SplitView
						leftPanel={
							<BenchmarkListComponent
								splitDetailOpen={benchmarksDetailOpen}
							/>
						}
						rightPanel={<BenchmarkDetailPanel onReloadKnowledgeGraph={handleReloadKnowledgeGraph} />}
						isRightPanelVisible={benchmarksDetailOpen}
						defaultRightWidth={35}
					/>
				);
			}

			case "measures": {
				const kgDetailOpen = editColumn && selectedKgColumn !== null;
				return (
					<SplitView
						leftPanel={
							<KGColumnListComponent
								kgColumnList={measureList}
								columnType={ColumnType.MEASURE}
								refreshKgColumns={fetchKgColumns}
								splitDetailOpen={kgDetailOpen}
							/>
						}
						rightPanel={
							<KGColumnDetailPanel
								onReloadKnowledgeGraph={handleReloadKnowledgeGraph}
								onSave={fetchKgColumns}
							/>
						}
						isRightPanelVisible={kgDetailOpen}
						defaultRightWidth={35}
					/>
				);
			}

			case "attributes": {
				const kgDetailOpen = editColumn && selectedKgColumn !== null;
				return (
					<SplitView
						leftPanel={
							<KGColumnListComponent
								kgColumnList={attributeList}
								columnType={ColumnType.ATTRIBUTE}
								refreshKgColumns={fetchKgColumns}
								splitDetailOpen={kgDetailOpen}
							/>
						}
						rightPanel={
							<KGColumnDetailPanel
								onReloadKnowledgeGraph={handleReloadKnowledgeGraph}
								onSave={fetchKgColumns}
							/>
						}
						isRightPanelVisible={kgDetailOpen}
						defaultRightWidth={35}
					/>
				);
			}

			case "time": {
				const kgDetailOpen = editColumn && selectedKgColumn !== null;
				return (
					<SplitView
						leftPanel={
							<KGColumnListComponent
								kgColumnList={temporalList}
								columnType={ColumnType.TEMPORAL}
								refreshKgColumns={fetchKgColumns}
								splitDetailOpen={kgDetailOpen}
							/>
						}
						rightPanel={
							<KGColumnDetailPanel
								onReloadKnowledgeGraph={handleReloadKnowledgeGraph}
								onSave={fetchKgColumns}
							/>
						}
						isRightPanelVisible={kgDetailOpen}
						defaultRightWidth={35}
					/>
				);
			}

			case "createModel":
				return (
					<OntologyMappingContainer
						modelInfo={modelInfo}
						updateFlag={false}
						onBack={() => navigate("/mkg/models")}
						onReloadModelList={fetchModelList}
					/>
				);

			case "updateModel":
				return (
					<OntologyMappingContainer
						updateFlag={true}
						onBack={() => navigate("/mkg/models")}
						onReloadModelList={fetchModelList}
						addedColumns={addedColumns}
						removedColumns={removedColumns}
					/>
				);

			case "ankoModel":
				return <ModelListContainer modelList={modelList} setModelList={setModelList} />;

			case "thoughtspotEmbed":
				return (
					<ThoughtSpotEmbed
						setModelInfo={setModelInfo}
						modelList={modelList}
						tsModelId={tsMatchTsId}
						setAddedColumns={setAddedColumns}
						setRemovedColumns={setRemovedColumns}
					/>
				);

			case "memory":
				return <MemoryConfigurationPanel />;

			default:
				return null;
		}
	};

	return (
		<>
			{renderMainPanelContent()}
			<DependenciesModal
				isOpen={showDependenciesModal}
				onClose={handleCloseDependenciesModal}
				dependencies={dependencies}
			/>
		</>
	);
};

export default ConfigPageMainPanel;
